import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useHousehold } from "@/contexts/HouseholdContext";
import { fmtCurrency, fmtDate, daysUntil } from "@/lib/format";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
    ChevronLeft, PiggyBank, Minus, Target, CalendarDays, History,
    ArrowDownToLine, ArrowUpFromLine, TrendingUp,
} from "lucide-react";

export default function FundDetail() {
    const { fid } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const cur = user?.currency || "USD";
    const { isMember, memberName } = useHousehold();

    const [fund, setFund] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(null); // 'deposit' | 'withdrawal' | null
    const [filter, setFilter] = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: f }, { data: h }] = await Promise.all([
                api.get(`/funds/${fid}`),
                api.get(`/funds/${fid}/contributions`),
            ]);
            setFund(f);
            setHistory(h);
        } catch (e) {
            // 402 = Free plan (Sinking Funds is Pro+) — /funds itself shows the upsell card.
            toast.error(e?.response?.status === 402 ? "Sinking Funds is a Pro feature" : "Fund not found");
            navigate("/funds");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [fid]);

    const stats = useMemo(() => {
        const deposits = history.filter(h => h.kind !== "withdrawal");
        const withdrawals = history.filter(h => h.kind === "withdrawal");
        const totalIn = deposits.reduce((s, h) => s + parseFloat(h.amount), 0);
        const totalOut = withdrawals.reduce((s, h) => s + parseFloat(h.amount), 0);
        return { totalIn, totalOut, depCount: deposits.length, wdCount: withdrawals.length };
    }, [history]);

    const filteredHistory = useMemo(() => {
        if (filter === "deposits") return history.filter(h => h.kind !== "withdrawal");
        if (filter === "withdrawals") return history.filter(h => h.kind === "withdrawal");
        return history;
    }, [history, filter]);

    if (loading || !fund) {
        return <div className="card-elev rounded-2xl p-10 text-center text-slate-400 text-sm">Loading…</div>;
    }

    const goal = parseFloat(fund.goal_amount) || 0;
    const cur_amt = parseFloat(fund.current_amount) || 0;
    const remaining = Math.max(0, goal - cur_amt);
    const pct = goal > 0 ? Math.min(100, (cur_amt / goal) * 100) : 0;
    const dleft = fund.due_date ? daysUntil(fund.due_date) : null;
    const color = fund.color || "#4F46E5";
    const radius = 56;
    const circ = 2 * Math.PI * radius;
    const dash = (pct / 100) * circ;
    const isOwner = fund.user_id === user.id;

    return (
        <div className="space-y-4" data-testid="fund-detail-page">
            <div className="flex items-center gap-3">
                <Link to="/funds" data-testid="back-to-funds" className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700">
                    <ChevronLeft className="w-5 h-5" />
                </Link>
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 mb-0.5">Sinking Fund</p>
                    <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 truncate flex items-center gap-2" data-testid="fund-name">
                        {fund.name}
                        {isMember && !isOwner ? (
                            <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-indigo-50 text-indigo-600 shrink-0">{memberName(fund.user_id)}'s</span>
                        ) : null}
                    </h1>
                </div>
            </div>

            {/* Summary card */}
            <div className="card-elev rounded-2xl p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-5 gap-6 items-center">
                <div className="flex items-center justify-center lg:col-span-2">
                    <div className="relative w-44 h-44">
                        <svg viewBox="0 0 130 130" className="w-full h-full -rotate-90">
                            <circle cx="65" cy="65" r={radius} fill="none" stroke="#F1F5F9" strokeWidth="12" />
                            <circle cx="65" cy="65" r={radius} fill="none" stroke={color} strokeWidth="12" strokeLinecap="round" strokeDasharray={`${dash} ${circ}`} className="transition-all duration-700" />
                        </svg>
                        <div className="absolute inset-0 grid place-items-center">
                            <div className="text-center">
                                <div className="font-display text-3xl font-extrabold text-slate-900" data-testid="fund-pct">{Math.round(pct)}%</div>
                                <div className="text-xs text-slate-500 mt-0.5">Saved</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="lg:col-span-3 space-y-4">
                    <div>
                        <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Saved so far</span>
                        <div className="font-display text-4xl sm:text-5xl font-extrabold text-slate-900 tabular-nums" data-testid="fund-current">{fmtCurrency(cur_amt, cur)}</div>
                        <div className="text-sm text-slate-500 mt-0.5">of {fmtCurrency(goal, cur)} goal · {fmtCurrency(remaining, cur)} to go</div>
                    </div>
                    <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-700">
                            <Target className="w-3.5 h-3.5" /> {fund.category}
                        </span>
                        {fund.due_date ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-indigo-50 text-indigo-700">
                                <CalendarDays className="w-3.5 h-3.5" /> {fmtDate(fund.due_date)}{dleft !== null ? ` · ${dleft >= 0 ? `${dleft}d left` : "Past due"}` : ""}
                            </span>
                        ) : null}
                    </div>
                    {fund.notes ? <p className="text-sm text-slate-600 leading-relaxed">{fund.notes}</p> : null}
                    {isOwner ? (
                        <div className="flex flex-wrap gap-2 pt-2">
                            <Button data-testid="dt-contribute" onClick={() => setOpenDialog("deposit")} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl gap-1.5 h-11 px-5">
                                <PiggyBank className="w-4 h-4" /> Contribute
                            </Button>
                            <Button data-testid="dt-withdraw" onClick={() => setOpenDialog("withdrawal")} variant="outline" className="rounded-xl gap-1.5 h-11 px-5 border-slate-200 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200">
                                <Minus className="w-4 h-4" /> Withdraw
                            </Button>
                        </div>
                    ) : (
                        <p className="text-xs text-slate-400 pt-2">Only {memberName(fund.user_id)} can add or withdraw funds here.</p>
                    )}
                </div>
            </div>

            {/* Stat row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <MiniStat icon={ArrowDownToLine} accent="emerald" label="Deposited" value={fmtCurrency(stats.totalIn, cur)} sub={`${stats.depCount} entries`} />
                <MiniStat icon={ArrowUpFromLine} accent="rose" label="Withdrawn" value={fmtCurrency(stats.totalOut, cur)} sub={`${stats.wdCount} entries`} />
                <MiniStat icon={TrendingUp} accent="indigo" label="Net" value={fmtCurrency(stats.totalIn - stats.totalOut, cur)} sub="Inflow − Outflow" />
                <MiniStat icon={Target} accent="amber" label="Remaining" value={fmtCurrency(remaining, cur)} sub={`${Math.round(pct)}% complete`} />
            </div>

            {/* History */}
            <div className="card-elev rounded-2xl p-6">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                    <div className="flex items-center gap-2.5">
                        <span className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 grid place-items-center"><History className="w-4 h-4" /></span>
                        <div>
                            <h3 className="font-display text-lg font-bold text-slate-900">Activity history</h3>
                            <p className="text-sm text-slate-500">{history.length} transactions</p>
                        </div>
                    </div>
                    <Tabs value={filter} onValueChange={setFilter}>
                        <TabsList className="bg-slate-100 rounded-xl p-1">
                            <TabsTrigger data-testid="hist-all" value="all" className="rounded-lg data-[state=active]:bg-white px-3">All</TabsTrigger>
                            <TabsTrigger data-testid="hist-dep" value="deposits" className="rounded-lg data-[state=active]:bg-white px-3">Deposits</TabsTrigger>
                            <TabsTrigger data-testid="hist-wd" value="withdrawals" className="rounded-lg data-[state=active]:bg-white px-3">Withdrawals</TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>

                {filteredHistory.length === 0 ? (
                    <EmptyState
                        icon={History}
                        title="No activity yet"
                        description="Make your first contribution to start building this fund."
                        action={isOwner ? <Button onClick={() => setOpenDialog("deposit")} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"><PiggyBank className="w-4 h-4 mr-1.5" /> Contribute</Button> : null}
                    />
                ) : (
                    <div className="divide-y divide-slate-100" data-testid="history-list">
                        {filteredHistory.map(h => {
                            const isWd = h.kind === "withdrawal";
                            return (
                                <div key={h.id} className="flex items-center gap-3 py-3.5">
                                    <span className={`w-10 h-10 rounded-xl grid place-items-center ${isWd ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"}`}>
                                        {isWd ? <ArrowUpFromLine className="w-4 h-4" /> : <ArrowDownToLine className="w-4 h-4" />}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-slate-900 truncate">{h.note || (isWd ? "Withdrawal" : "Contribution")}</div>
                                        <div className="text-xs text-slate-500">{fmtDate(h.occurred_at, { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</div>
                                    </div>
                                    <div className={`font-display font-bold tabular-nums ${isWd ? "text-rose-600" : "text-emerald-600"}`}>
                                        {isWd ? "−" : "+"}{fmtCurrency(h.amount, cur)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <ContribDialog
                open={!!openDialog}
                kind={openDialog}
                fund={fund}
                onClose={() => setOpenDialog(null)}
                onSaved={load}
            />
        </div>
    );
}

function MiniStat({ icon: Icon, accent, label, value, sub }) {
    const map = {
        emerald: "bg-emerald-50 text-emerald-600",
        rose: "bg-rose-50 text-rose-600",
        indigo: "bg-indigo-50 text-indigo-600",
        amber: "bg-amber-50 text-amber-600",
    };
    return (
        <div className="card-elev rounded-2xl p-5">
            <div className="flex items-start justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{label}</span>
                <span className={`w-8 h-8 rounded-lg grid place-items-center ${map[accent]}`}><Icon className="w-4 h-4" /></span>
            </div>
            <div className="font-display text-xl font-extrabold text-slate-900 tabular-nums">{value}</div>
            <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
        </div>
    );
}

function ContribDialog({ open, kind, fund, onClose, onSaved }) {
    const [amount, setAmount] = useState("");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const { user } = useAuth();
    const cur = user?.currency || "USD";

    useEffect(() => { if (open) { setAmount(""); setNote(""); } }, [open]);
    if (!open || !fund) return null;

    const isWd = kind === "withdrawal";
    const current = parseFloat(fund.current_amount) || 0;

    const submit = async () => {
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
        if (isWd && amt > current) { toast.error(`You only have ${fmtCurrency(current, cur)} saved`); return; }
        try {
            setSaving(true);
            await api.post(`/funds/${fund.id}/contributions`, { amount: amt, kind: isWd ? "withdrawal" : "deposit", note: note || null });
            toast.success(isWd ? "Withdrawal recorded" : "Contribution added");
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-md rounded-2xl" data-testid="dt-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-xl">
                        {isWd ? "Withdraw from" : "Contribute to"} {fund.name}
                    </DialogTitle>
                    <p className="text-xs text-slate-500">Currently saved: <span className="font-semibold text-slate-700 tabular-nums">{fmtCurrency(current, cur)}</span></p>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label className="text-xs font-semibold text-slate-700">Amount</Label>
                        <Input data-testid="dt-amount" type="number" step="0.01" autoFocus value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs font-semibold text-slate-700">Note (optional)</Label>
                        <Input data-testid="dt-note" value={note} onChange={e => setNote(e.target.value)} placeholder={isWd ? "Used for car repair" : "Bonus"} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button data-testid="dt-save" disabled={saving} onClick={submit}
                        className={`text-white ${isWd ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>
                        {isWd ? "Record withdrawal" : "Save contribution"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
